import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import TabsGallery, { TabsGalleryRef } from 'components/TabsGallery';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import React from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAddCoiCheckMutation, useGetCoiCheckSummaryByIdQuery } from 'store/api/coi-check';
import { t } from 'utils/getTranslations';
import { useGoBack } from 'utils/useNavigateWithReturnState';
import CoiCheckDetails from './CoiCheckDetails';
import CoiCheckView from './views/CoiCheckView';
import PreviousCoiChecksView from './views/PreviousCoiChecksView';

const coiCheckDetailsTabNumber = 2;

const CoiCheckPage = (): JSX.Element => {
    const { id: entityId } = useParams();
    const goBack = useGoBack('/coi-checks');
    const tabsGalleryRef = React.createRef<TabsGalleryRef>();
    const navigate = useNavigate();
    const [addCoiCheck, { isLoading: isCreating }] = useAddCoiCheckMutation();

    const { data: coiCheckSummary, isLoading } = useGetCoiCheckSummaryByIdQuery(
        entityId ? { id: entityId, isManuallySelected: false } : skipToken
    );

    if (isLoading) {
        return <Loader />;
    }

    // The details tab has no search bar beside it, so the new check opens on a page of its own. A failure
    // is already toasted by the API layer.
    const runSuggestedCheck = (searchTerm: string): void => {
        addCoiCheck({ searchTerm })
            .unwrap()
            .then((created) => navigate(`/coi-check/${created.id}`))
            .catch(() => undefined);
    };

    const tabComponents = [
        {
            id: 'coi-check',
            label: t('coi.coi-check'),
            component: <CoiCheckView />,
        },
        {
            id: 'previous-coi-checks',
            label: t('coi.previous-coi-checks'),
            component: <PreviousCoiChecksView />,
        },
        {
            id: 'coi-check-details',
            label: t('coi.coi-check-details'),
            component: (
                <div className="px-[26px] pt-4 pb-5">
                    {coiCheckSummary && (
                        // Withdrawn while the check runs, so a second press cannot create a second check.
                        <CoiCheckDetails
                            summary={coiCheckSummary}
                            onDidYouMean={isCreating ? undefined : runSuggestedCheck}
                        />
                    )}
                </div>
            ),
        },
    ];

    return (
        <PaddedLayout>
            <PageHeader titleId="coi.coi-check" onBack={goBack} />
            <div className="h-4" />
            <TabsGallery
                ref={tabsGalleryRef}
                tabs={tabComponents}
                initialActiveTab={coiCheckDetailsTabNumber}
                tabGroupId="coi-check-page"
                trackTabsInHistoryState
            />
        </PaddedLayout>
    );
};

export default CoiCheckPage;
